import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  FileText,
  TrendingUp,
  ArrowRight,
  RefreshCw,
  Eye,
  XCircle,
  UserPlus,
  Shield,
  Database
} from 'lucide-react';
import { toast } from 'sonner';

const EmpresasDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [onboardingPendiente, setOnboardingPendiente] = useState([]);
  const [empresasConAlertas, setEmpresasConAlertas] = useState([]);
  const [cambiosRecientes, setCambiosRecientes] = useState([]);
  const [cierrePeriodoData, setCierrePeriodoData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, onboardingRes, alertasRes, cierreRes] = await Promise.all([
        api.get('/empresas/stats').catch(() => ({ data: null })),
        api.get('/empresas/onboarding/pending').catch(() => ({ data: [] })),
        api.get('/empresas/con-alertas').catch(() => ({ data: [] })),
        api.get('/cierre-periodo/dashboard').catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data || {
        total: 0,
        activas: 0,
        en_revision: 0,
        pendientes_aprobacion: 0,
        con_alertas: 0,
        nuevas_mes: 0
      });
      setOnboardingPendiente(onboardingRes.data || []);
      setEmpresasConAlertas(alertasRes.data || []);
      setCierrePeriodoData(cierreRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarCierre = async (periodo, tipo, completo) => {
    try {
      await api.post('/cierre-periodo/marcar', {
        periodo,
        tipo,
        completo,
        comentarios: null
      });
      toast.success(`Período ${periodo} ${completo ? 'cerrado' : 'reabierto'}`);
      // Recargar datos de cierre
      const res = await api.get('/cierre-periodo/dashboard');
      setCierrePeriodoData(res.data);
    } catch (error) {
      toast.error('Error al actualizar estado del período');
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'ACTIVA': 'bg-green-100 text-green-700',
      'INACTIVO': 'bg-slate-100 text-slate-700',
      'APROBADA': 'bg-emerald-100 text-emerald-700',
      'EN_REVISION': 'bg-blue-100 text-blue-700',
      'PENDIENTE_APROBACION': 'bg-purple-100 text-purple-700',
      'RECHAZADA': 'bg-red-100 text-red-700',
      'INFO_ADICIONAL': 'bg-orange-100 text-orange-700',
      'BORRADOR': 'bg-yellow-100 text-yellow-700'
    };
    return colors[estado] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="empresas-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="text-blue-600" />
            Dashboard de Empresas
          </h1>
          <p className="text-slate-600 mt-1">
            Vista general del estado de las empresas en el sistema
          </p>
        </div>
        <Button onClick={loadDashboard} variant="outline">
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Acciones Rápidas */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="text-slate-600" />
          Acciones Rápidas
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate('/m/empresas/empresas')}
          >
            <Building2 size={24} className="text-blue-600" />
            <span>Ver Empresas</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate('/m/empresas/onboarding')}
          >
            <UserPlus size={24} className="text-purple-600" />
            <span>Solicitudes Onboarding</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate('/m/empresas/padron-interno')}
          >
            <Users size={24} className="text-teal-600" />
            <span>Padrón de Clientes</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate('/m/empresas/movimientos')}
          >
            <TrendingUp size={24} className="text-green-600" />
            <span>Movimientos</span>
          </Button>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card 
          className="p-4 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/m/empresas/empresas')}
        >
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="text-blue-600" size={20} />
            <span className="text-sm font-medium text-slate-700">Total</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">{stats?.total || 0}</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:shadow-md transition-shadow bg-green-50"
          onClick={() => navigate('/m/empresas/empresas?estado=APROBADA')}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={20} />
            <span className="text-sm font-medium text-green-700">Activas</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{stats?.activas || 0}</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:shadow-md transition-shadow bg-blue-50"
          onClick={() => navigate('/m/empresas/empresas?estado=EN_REVISION')}
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="text-blue-600" size={20} />
            <span className="text-sm font-medium text-blue-700">En Revisión</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">{stats?.en_revision || 0}</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:shadow-md transition-shadow bg-purple-50"
          onClick={() => navigate('/m/empresas/onboarding')}
        >
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="text-purple-600" size={20} />
            <span className="text-sm font-medium text-purple-700">Onboarding</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">{stats?.pendientes_aprobacion || 0}</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:shadow-md transition-shadow bg-amber-50"
          onClick={() => {}}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-amber-600" size={20} />
            <span className="text-sm font-medium text-amber-700">Con Alertas</span>
          </div>
          <p className="text-3xl font-bold text-amber-600">{stats?.con_alertas || 0}</p>
        </Card>

        <Card className="p-4 bg-teal-50">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="text-teal-600" size={20} />
            <span className="text-sm font-medium text-teal-700">Nuevas (Mes)</span>
          </div>
          <p className="text-3xl font-bold text-teal-600">{stats?.nuevas_mes || 0}</p>
        </Card>
      </div>

      {/* Widget de Cierre de Período */}
      {cierrePeriodoData && (
        <Card className="p-6 bg-gradient-to-r from-cyan-50 to-teal-50 border-teal-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Database size={22} className="text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Cierre de Períodos de Datos</h2>
                <p className="text-sm text-slate-500">Marque los períodos cuando los datos estén completos</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {cierrePeriodoData.periodos?.slice(0, 6).map((p) => {
              const meses = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
              const mes = parseInt(p.periodo.substring(4, 6));
              const año = p.periodo.substring(2, 4);
              const periodoLabel = `${meses[mes]} '${año}`;
              
              return (
                <div key={p.periodo} className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-800 text-sm">{periodoLabel}</span>
                    <Badge 
                      className={`text-xs ${
                        p.cierre.periodo_completo ? 'bg-green-100 text-green-700' :
                        p.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {p.cierre.periodo_completo ? '✓' : p.estado === 'vencido' ? '!' : '○'}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre.movimientos_completo)}
                      className={`flex-1 p-1.5 rounded text-xs font-medium transition-colors ${
                        p.cierre.movimientos_completo 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      title="Movimientos completos"
                    >
                      Mov
                    </button>
                    <button
                      onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre.padron_completo)}
                      className={`flex-1 p-1.5 rounded text-xs font-medium transition-colors ${
                        p.cierre.padron_completo 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      title="Padrón completo"
                    >
                      Pad
                    </button>
                  </div>
                  {p.dias_para_vencer !== undefined && p.dias_para_vencer < 10 && !p.cierre.periodo_completo && (
                    <p className={`text-xs mt-1 text-center ${p.dias_para_vencer < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                      {p.dias_para_vencer < 0 ? `Vencido hace ${Math.abs(p.dias_para_vencer)}d` : `${p.dias_para_vencer}d para vencer`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Onboarding Pendiente */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <UserPlus className="text-purple-600" />
              Onboarding Pendiente
            </h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/m/empresas/onboarding')}
            >
              Ver Todos
              <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>

          {onboardingPendiente.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle size={40} className="mx-auto mb-2 opacity-50" />
              <p>No hay solicitudes de onboarding pendientes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {onboardingPendiente.slice(0, 5).map((empresa, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/m/empresas/onboarding/${empresa.id}`)}
                >
                  <div>
                    <p className="font-medium text-slate-900">{empresa.razon_social}</p>
                    <p className="text-sm text-slate-500">{empresa.cuit}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={getEstadoColor(empresa.estado)}>
                      {empresa.estado?.replace(/_/g, ' ')}
                    </Badge>
                    {empresa.dias_pendiente > 0 && (
                      <p className="text-xs text-slate-400 mt-1">{empresa.dias_pendiente} días</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Empresas con Alertas */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="text-amber-600" />
              Empresas con Alertas Activas
            </h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/m/monitoreo-transaccional/alerts')}
            >
              Ver Alertas
              <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>

          {empresasConAlertas.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Shield size={40} className="mx-auto mb-2 opacity-50" />
              <p>No hay empresas con alertas activas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {empresasConAlertas.slice(0, 5).map((empresa, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/m/empresas/empresas/${empresa.id}`)}
                >
                  <div>
                    <p className="font-medium text-slate-900">{empresa.razon_social}</p>
                    <p className="text-sm text-slate-500">{empresa.cuit}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-amber-100 text-amber-700">
                      {empresa.alertas_count} alertas
                    </Badge>
                    {empresa.severidad_max && (
                      <Badge className={
                        empresa.severidad_max === 'CRITICA' ? 'bg-red-100 text-red-700 ml-1' :
                        empresa.severidad_max === 'ALTA' ? 'bg-orange-100 text-orange-700 ml-1' :
                        'bg-yellow-100 text-yellow-700 ml-1'
                      }>
                        {empresa.severidad_max}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

    </div>
  );
};

export default EmpresasDashboard;
